/**
 * ZON-275 — Recent news for one school in the My Schools detail pane: the 3
 * newest items, "Show more" in place. Not rendered at all when there is
 * nothing to show, so a quiet program does not get an empty box.
 */
import { useEffect, useState } from "react";
import { trackSchoolNewsAction, trackSchoolNewsCardViewed } from "@/analytics/analytics";
import { sourceProps } from "@/analytics/source";
import { NewsRow } from "@/components/news/NewsCards";
import { useSchoolNewsEnabled } from "@/hooks/useSchoolNewsEnabled";
import { fetchTeamNews, type NewsItem } from "@/services/schoolNews";

const FIRST_PAGE = 3;
const MORE_PAGE = 10;

export function SchoolNewsCard({ teamId }: { teamId: string | null | undefined }) {
  const enabled = useSchoolNewsEnabled();
  const [items, setItems] = useState<NewsItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setItems([]);
    setNextCursor(null);
    if (!enabled || !teamId) return;
    let cancelled = false;
    fetchTeamNews(teamId, { limit: FIRST_PAGE })
      .then(page => {
        if (cancelled || !page.enabled) return;
        setItems(page.items);
        setNextCursor(page.nextCursor);
        // Counted only when the card actually shows: a quiet school renders nothing.
        if (page.items.length > 0) {
          trackSchoolNewsCardViewed({
            teamId,
            itemCount: page.items.length,
            ...sourceProps(window.location.search),
          });
        }
      })
      .catch(error => console.error("Failed to load school news:", error));
    return () => {
      cancelled = true;
    };
  }, [enabled, teamId]);

  function track(action: "read" | "show_more", type?: string) {
    if (!teamId) return;
    trackSchoolNewsAction({
      action,
      ...(type ? { type } : {}),
      surface: "school",
      teamId,
      ...sourceProps(window.location.search),
    });
  }

  async function showMore() {
    if (!teamId || !nextCursor || loadingMore) return;
    track("show_more");
    setLoadingMore(true);
    try {
      const page = await fetchTeamNews(teamId, { limit: MORE_PAGE, before: nextCursor });
      setItems(current => [...current, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (error) {
      console.error("Failed to load more school news:", error);
    } finally {
      setLoadingMore(false);
    }
  }

  if (!enabled || !teamId || items.length === 0) return null;

  return (
    <section className="rounded-2xl border border-border bg-white p-4">
      <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-subtle">
        School Watch · Recent news
      </p>
      <div className="mt-1.5">
        {items.map(item => (
          <NewsRow
            key={item.id}
            item={item}
            showSchool={false}
            onRead={read => track("read", read.type)}
          />
        ))}
      </div>
      {nextCursor && (
        <button
          type="button"
          disabled={loadingMore}
          onClick={() => void showMore()}
          className="mt-1 text-[11px] font-bold text-primary-text hover:underline disabled:opacity-50"
        >
          {loadingMore ? "Loading…" : "Show more"}
        </button>
      )}
    </section>
  );
}
